import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  Alert,
  InputAdornment,
  Card,
  CardContent,
  CircularProgress,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import UploadIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { COMPANY_SIZES } from '../../../constants';
import { hostname } from '../../../utils';

const DetailsContainer = styled(Box)(({ theme }) => ({
  width: '100%',
}));

interface CompanyDetailsStepProps {
  formData: any;
  updateFormData: (field: string, value: string) => void;
  onNext: () => void;
  onBack: () => void;
  appName: string;
  getToken?: () => Promise<string>;
}

export default function CompanyDetailsStep({ 
  formData, 
  updateFormData, 
  onNext, 
  onBack, 
  appName,
  getToken 
}: CompanyDetailsStepProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.size) {
      newErrors.size = 'Please select company size';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const handleLogoUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !getToken) return;

    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please upload an image file (PNG, JPG, etc.)');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('Image size should be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    setUploadError(null);
    
    try {
      const token = await getToken();
      
      // Convert image to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64String = reader.result as string;
          
          // Create image element to get dimensions
          const img = new Image();
          img.onload = async () => {
            const imageData = {
              name: `Company Logo`,
              category: 'company-logo',
              url: base64String,
              mimeType: file.type,
              width: img.width.toString(),
              height: img.height.toString(),
              fileSize: file.size.toString(),
              public: true,
            };

            // Upload the image to platform-images
            const uploadResponse = await axios.post(
              `${hostname}/platform-images`,
              imageData,
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (uploadResponse.status === 201) {
              const responseData = uploadResponse.data;
              // Use publicUrl from backend response (respects whitelabel domains)
              const publicUrl = responseData.publicUrl || responseData.url;
              updateFormData('logoUrl', publicUrl);
            }
          };
          img.src = base64String;
        } catch (err) {
          console.error('Error uploading logo:', err);
          setUploadError('Failed to upload image. Please try again.');
          setUploadingLogo(false);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error reading file:', err);
      setUploadError('Failed to read image file. Please try again.');
      setUploadingLogo(false);
    } finally {
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = () => {
    updateFormData('logoUrl', '');
  };

  return (
    <DetailsContainer>
      <Fade in={true} timeout={800}>
        <Box>
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: 'text.primary'
              }}
            >
              A few more details
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ lineHeight: 1.6 }}
            >
              This helps us create the perfect AI team for your organization
            </Typography>
          </Box>

          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth required error={!!errors.size}>
              <InputLabel>Company Size</InputLabel>
              <Select
                value={formData.size}
                label="Company Size"
                onChange={(e) => updateFormData('size', e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <PeopleIcon color={errors.size ? 'error' : 'action'} />
                  </InputAdornment>
                }
              >
                {COMPANY_SIZES.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
              {errors.size && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {errors.size}
                </Typography>
              )}
            </FormControl>

            <TextField
              label="Location"
              value={formData.location}
              onChange={(e) => updateFormData('location', e.target.value)}
              error={!!errors.location}
              helperText={errors.location || 'Where is your company based? (e.g., San Francisco, CA)'}
              fullWidth
              required
              placeholder="City, State/Country"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon color={errors.location ? 'error' : 'action'} />
                  </InputAdornment>
                ),
              }}
            />

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Company Logo (Optional)
              </Typography>
              <Card variant="outlined" sx={{ p: 2 }}>
                {formData.logoUrl ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <img
                      src={formData.logoUrl}
                      alt="Company logo"
                      style={{
                        maxHeight: 60,
                        maxWidth: 120,
                        objectFit: 'contain',
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton
                      onClick={handleRemoveLogo}
                      disabled={uploadingLogo}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    {uploadingLogo ? (
                      <CircularProgress size={24} />
                    ) : (
                      <>
                        <Button
                          startIcon={<UploadIcon />}
                          onClick={handleLogoUploadClick}
                          variant="outlined"
                          disabled={uploadingLogo || !getToken}
                          size="small"
                        >
                          Upload Logo
                        </Button>
                        <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                          PNG, JPG up to 2MB • Suggested size: 200x200
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
                {uploadError && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {uploadError}
                  </Typography>
                )}
              </Card>
            </Box>

            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {Object.keys(errors).length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Please fill in all required fields to continue.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={onBack}
                sx={{ px: 3 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{ 
                  px: 4,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Continue
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </DetailsContainer>
  );
}